import { AfterViewInit, Component, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, ActivationEnd, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as Sentry from '@sentry/browser';
import { fromEvent, merge } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import config from '../config';
import { environment } from '../environments/environment';
import { VersionCheckService } from './core/services/common/version-check.service';
import { ParamsHelperService } from './core/services/helper/params.service';
import { HIPAASessionTimeoutService } from './core/services/hipaa-session-timeout/hipaa-session-timeout.service';
import { LanguageService } from './core/services/language/language.service';
import { ProviderService } from './core/services/provider/provider.service';

export const SENTRY_SESSION_ID_KEY = 'sentryContextSessionId';

declare const gtag: (event: string, view: string, conf: any)=> void;

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
    title = 'app';
    checkTimeout;

    timeoutVersionCheck: number = config.TIMEOUT_VERSION_CHECK;

    constructor(
        private versionCheckService: VersionCheckService,
        private languageService: LanguageService,
        private router: Router,
        iconRegistry: MatIconRegistry,
        sanitizer: DomSanitizer,
        private paramsHelperService: ParamsHelperService,
        private activatedRoute: ActivatedRoute,
        private providerService: ProviderService,
        private hipaaSessionTimeoutService: HIPAASessionTimeoutService,
    ) {
        this.hipaaSessionTimeoutService.initializeSessionManagement();
        this.setSentrySessionId();
        this.checkTimeout = new Date().getTime();
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
            this.processSSOd(event.url);
            if (typeof gtag !== 'undefined') {
                gtag('event', 'page_view', {

                    page_path: event.urlAfterRedirects,
                });
            }
        });
        this.router.events
            .pipe(
                filter((e) => e instanceof ActivationEnd && Object.keys(e.snapshot.params).length > 0),
                map((e) => e instanceof ActivationEnd ? e.snapshot.params : {}),
            )
            .subscribe((params) => {
                this.paramsHelperService.setQueryParams(params);
            });
        iconRegistry.addSvgIcon(
            'attached-video',
            sanitizer.bypassSecurityTrustResourceUrl('assets/shared/attachment/camera.svg'),
        );
        iconRegistry.addSvgIcon(
            'attached-audio',
            sanitizer.bypassSecurityTrustResourceUrl('assets/shared/attachment/mic.svg'),
        );
        iconRegistry.addSvgIcon(
            'attached-image',
            sanitizer.bypassSecurityTrustResourceUrl('assets/shared/attachment/image.svg'),
        );
        iconRegistry.addSvgIcon(
            'paper-clip',
            sanitizer.bypassSecurityTrustResourceUrl('assets/shared/attachment/paper_clip.svg'),
        );
        iconRegistry.addSvgIcon(
            'ohp-building-with-clock',
            sanitizer.bypassSecurityTrustResourceUrl('assets/icons/dashboard-navigation/building-with-clock.svg'),
        );
        iconRegistry.addSvgIcon(
            'ohp-multiple-users',
            sanitizer.bypassSecurityTrustResourceUrl('assets/icons/dashboard-navigation/multiple-users.svg'),
        );
        iconRegistry.addSvgIcon(
            'ohp-stethoscope',
            sanitizer.bypassSecurityTrustResourceUrl('assets/icons/dashboard-navigation/stethoscope.svg'),
        );
        iconRegistry.addSvgIcon(
            'ohp-calendar',
            sanitizer.bypassSecurityTrustResourceUrl('assets/icons/dashboard-navigation/calendar.svg'),
        );
        iconRegistry.addSvgIcon(
            'nothing-to-see',
            sanitizer.bypassSecurityTrustResourceUrl('assets/main/nothing-to-see-icon.svg'),
        );
        iconRegistry.addSvgIcon(
            'no-care-plan',
            sanitizer.bypassSecurityTrustResourceUrl('assets/icons/care-plan-no-data.svg'),
        );
        iconRegistry.addSvgIcon(
            'homeIcon',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/home-icon.svg'),
        );
        iconRegistry.addSvgIcon(
            'teamIcon',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/ic-team.svg'),
        );
        iconRegistry.addSvgIcon(
            'downArrow',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/down-arrow.svg'),
        );
        iconRegistry.addSvgIcon(
            'messageDarkIcon',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/message-dark-icon.svg'),
        );
        iconRegistry.addSvgIcon(
            'email',
            sanitizer.bypassSecurityTrustResourceUrl('assets/covid-clinic/email.svg'),
        );
        iconRegistry.addSvgIcon(
            'appointmentIcon',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/appointment-icon.svg'),
        );
        iconRegistry.addSvgIcon(
            'allServicesIcon',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/all-services-icon.svg'),
        );
        iconRegistry.addSvgIcon(
            'logo',
            sanitizer.bypassSecurityTrustResourceUrl('assets/branding/logo_full.svg'),
        );
        iconRegistry.addSvgIcon(
            'arrow-down',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/arrow_down.svg'),
        );
        iconRegistry.addSvgIcon(
            'folder',
            sanitizer.bypassSecurityTrustResourceUrl('assets/icons/capacitygrid/folder.svg'),
        );
        iconRegistry.addSvgIcon(
            'check',
            sanitizer.bypassSecurityTrustResourceUrl('assets/icons/capacitygrid/check.svg'),
        );
        iconRegistry.addSvgIcon(
            'list-alt',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/list-alt.svg'),
        );
        iconRegistry.addSvgIcon(
            'clearFilter',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/clear_filter.svg'),
        );
        iconRegistry.addSvgIcon(
            'emptyStateTablets',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/empty_state_tablets.svg'),
        );
        iconRegistry.addSvgIcon(
            'editIcon',
            sanitizer.bypassSecurityTrustResourceUrl('assets/main/temp/edit-icon.svg'),
        );
        iconRegistry.addSvgIcon(
            'ic-assignments',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/ic-assignments.svg'),
        );
        iconRegistry.addSvgIcon(
            'ic-assignments',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/ic-assignments.svg'),
        );
        iconRegistry.addSvgIcon(
            'list-alt',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/list-alt.svg'),
        );
        iconRegistry.addSvgIcon(
            'list-alt',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/list-alt.svg'),
        );
        iconRegistry.addSvgIcon(
            'ic-book',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/ic-book.svg'),
        );
        iconRegistry.addSvgIcon(
            'ic-family',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/ic-family.svg'),
        );
        iconRegistry.addSvgIcon(
            'ic-home',
            sanitizer.bypassSecurityTrustResourceUrl('assets/svg/ic-home.svg'),
        );
        this.lazyLoadAgGrid().then();
    }

    ngOnInit(): void {
        const keypress$ = fromEvent(document, 'keypress');
        const mouseUp$ = fromEvent(document, 'mouseup');

        merge(keypress$, mouseUp$)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                const now = new Date().getTime();
                const timeout = this.timeoutVersionCheck * 60 * 1000;

                if (this.checkTimeout + timeout < now) {
                    this.checkTimeout = now;

                    if (environment.versionCheckURL && environment.environmentType !== 'local') {
                        this.versionCheckService.initVersionCheck(environment.versionCheckURL);
                    }
                }
            });
    }

    async lazyLoadAgGrid() {
        const { LicenseManager } = await import('ag-grid-enterprise');

        LicenseManager.setLicenseKey(`Using_this_AG_Grid_Enterprise_key_( AG-043764 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Composite Apps Inc. )_is_granted_a_( Multiple Applications )_Developer_License_for_( 10 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 3 )_Production_Environments___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 17 August 2024 )____[v2]_MTcyMzg0OTIwMDAwMA==db9796529ff9a3cf4886e4ef8e64db5e`);
    }

    setSentrySessionId() {
        try {
            const sessionId = uuid();

            window[SENTRY_SESSION_ID_KEY] = sessionId;
            Sentry.setTag('session_id', sessionId);

        } catch (e) {
            console.error(e);
            Sentry.captureException(e);
        }
    }

    ngAfterViewInit(): void {
        this.languageService.init();
    }

    processSSOd(url: string) {
        if (url.includes('?student_id=')) {
            const urlArr = url.split('?student_id=');
            const student_id = urlArr.pop();

            localStorage.setItem('aeries_student_id', student_id);
            if (!url.includes('/provider/dashboard')) {
                const new_url = `${ this.providerService.providerSSOdUrl }${ student_id }`;

                this.router.navigateByUrl(new_url).then();
            }

        }
    }
}
